import haversineDistance from "haversine-distance";
import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import usePlacesAutocomplete, { SetValue } from "use-places-autocomplete";

import { CompanySiteType } from "../types/company-site";

export interface StoreLocatorContextValue {
  selectedStore: Queries.CompanySiteDetailDataFragment | null;
  selectedStoreId: string | null;
  setSelectedStoreId: Dispatch<SetStateAction<string | null>>;
  allStores: Queries.CompanySiteDetailDataFragment[];
  filteredStores: Queries.CompanySiteDetailDataFragment[];
  map: google.maps.Map | null;
  setMap: Dispatch<SetStateAction<google.maps.Map | null>>;
  suggestions: google.maps.places.AutocompletePrediction[];
  setSearchValue: SetValue;
  searchValue: string;
}

export const StoreLocatorContext = createContext<StoreLocatorContextValue | null>(null);

export type OnSelectStoreCallback = (
  selectedStore: Queries.CompanySiteDetailDataFragment | null,
  map: google.maps.Map | null
) => void;

export interface StoreLocatorProviderProps {
  stores: Queries.CompanySiteDetailDataFragment[];
  onSelectStore?: OnSelectStoreCallback;
}

export interface StoresListFilters {
  types: Set<CompanySiteType>;
  latitude: number | null;
  longitude: number | null;
}

export function StoreLocatorProvider({
  stores,
  children,
  onSelectStore,
}: PropsWithChildren<StoreLocatorProviderProps>) {
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const filtersForm = useForm<StoresListFilters>({
    defaultValues: {
      types: new Set(Object.values(CompanySiteType)),
      latitude: null,
      longitude: null,
    },
  });

  const { watch } = filtersForm;

  const { types: filteredTypes, latitude, longitude } = watch();

  const filteredStores = useMemo(() => {
    const toReturn = stores.filter((store) => {
      if (!store.lat || !store.lng) return false;
      if (!store.siteType || !filteredTypes.has(store.siteType as CompanySiteType)) return false;
      return true;
    });

    if (latitude !== null && longitude !== null) {
      const sortedStores = toReturn
        .map((store) => {
          const dist = haversineDistance(
            { latitude, longitude },
            { latitude: store.lat!, longitude: store.lng! }
          );
          return { store, dist };
        })
        .sort((a, b) => {
          return a.dist < b.dist ? -1 : 1;
        })
        .map(({ store }) => store);

      if (sortedStores[0]?.lat && sortedStores[0]?.lng) {
        map?.setCenter({
          lat: sortedStores[0].lat,
          lng: sortedStores[0].lng,
        });
      }

      return sortedStores;
    } else {
      return toReturn;
    }
  }, [stores, filteredTypes, latitude, map, longitude]);

  const selectedStore = useMemo(() => {
    return stores.find(({ id }) => id === selectedStoreId) ?? null;
  }, [selectedStoreId, stores]);

  // if selected store is excluded by filters, unselect it
  useEffect(() => {
    if (selectedStore && !filteredStores.find((s) => s.id === selectedStore.id)) {
      setSelectedStoreId(null);
    }
  }, [selectedStore, filteredStores]);

  // Center map on init (by default on HQ or first store)
  const isMapCentered = useRef(false);
  useEffect(() => {
    const hqStore = filteredStores.find((store) => store.isHQ);
    const defaultStore = hqStore ?? filteredStores[0];
    const defaultCenter = defaultStore
      ? { lat: defaultStore.lat ?? 0, lng: defaultStore.lng ?? 0 }
      : undefined;
    if (defaultCenter && map && !isMapCentered.current) {
      map.setCenter(defaultCenter);
      isMapCentered.current = true;
    }
  }, [filteredStores, map]);

  useEffect(() => {
    onSelectStore?.(selectedStore, map);
  }, [selectedStore, map, onSelectStore]);

  const {
    suggestions: { data: suggestions },
    setValue: setSearchValue,
    value: searchValue,
  } = usePlacesAutocomplete({
    debounce: 250,
  });

  const value = useMemo<StoreLocatorContextValue>(
    () => ({
      selectedStoreId,
      setSelectedStoreId,
      selectedStore,
      filteredStores,
      allStores: stores,
      map,
      setMap,
      suggestions,
      setSearchValue,
      searchValue,
    }),
    [
      stores,
      map,
      setMap,
      suggestions,
      setSearchValue,
      searchValue,
      filteredStores,
      selectedStoreId,
      selectedStore,
      setSelectedStoreId,
    ]
  );

  return (
    <StoreLocatorContext.Provider value={value}>
      <FormProvider {...filtersForm}>{children}</FormProvider>
    </StoreLocatorContext.Provider>
  );
}
